import { useContext, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { UserContext } from "../context/user";
import {
  getScreensaverSettings,
  normalizeScreensaverSettings,
  SCREENSAVER_DEFAULTS,
  SCREENSAVER_CHANGED_EVENT,
  SHOW_SCREENSAVER_EVENT,
} from "@/lib/screensaver";

function isLightColor(color) {
  if (typeof color !== "string") return false;

  const hex = color.replace("#", "");
  const value = hex.length === 3
    ? hex.split("").map((channel) => `${channel}${channel}`).join("")
    : hex;
  const red = Number.parseInt(value.slice(0, 2), 16);
  const green = Number.parseInt(value.slice(2, 4), 16);
  const blue = Number.parseInt(value.slice(4, 6), 16);

  if ([red, green, blue].some(Number.isNaN)) return false;

  return (red * 299 + green * 587 + blue * 114) / 1000 > 150;
}

export function Screensaver() {
  const { shop } = useContext(UserContext);
  const [settings, setSettings] = useState(() => getScreensaverSettings());
  const [previewSettings, setPreviewSettings] = useState(null);
  const [visible, setVisible] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [imageFailed, setImageFailed] = useState(false);
  const timerRef = useRef(null);
  const visibleRef = useRef(false);
  const scheduleRef = useRef(() => {});
  const dismissRef = useRef(() => {});

  const displayedSettings = previewSettings || settings;
  const backgroundColor =
    displayedSettings.backgroundColor || SCREENSAVER_DEFAULTS.backgroundColor;
  const imageSource = /^https?:\/\//i.test(displayedSettings.imageUrl)
    ? displayedSettings.imageUrl
    : "";
  const hasImage = Boolean(imageSource && !imageFailed);
  const lightBackground = !hasImage && isLightColor(backgroundColor);
  const foregroundColor = lightBackground ? "#171717" : "#ffffff";
  const mutedColor = lightBackground
    ? "rgba(23, 23, 23, 0.65)"
    : "rgba(255, 255, 255, 0.65)";
  const subtleColor = lightBackground
    ? "rgba(23, 23, 23, 0.45)"
    : "rgba(255, 255, 255, 0.45)";
  const lineColor = lightBackground
    ? "rgba(23, 23, 23, 0.2)"
    : "rgba(255, 255, 255, 0.2)";
  const overlayColor = lightBackground
    ? "rgba(255, 255, 255, 0.12)"
    : "rgba(0, 0, 0, 0.35)";

  scheduleRef.current = () => {
    window.clearTimeout(timerRef.current);

    if (!settings.enabled || document.hidden || visibleRef.current) return;

    timerRef.current = window.setTimeout(() => {
      visibleRef.current = true;
      setVisible(true);
    }, settings.intervalMinutes * 60 * 1000);
  };

  dismissRef.current = () => {
    if (!visibleRef.current) return;
    visibleRef.current = false;
    setPreviewSettings(null);
    setVisible(false);
    scheduleRef.current();
  };

  useEffect(() => {
    const handleSettingsChanged = () => {
      const nextSettings = getScreensaverSettings();
      setSettings(nextSettings);
      setPreviewSettings(null);
      setImageFailed(false);

      if (!nextSettings.enabled) {
        dismissRef.current();
      } else {
        scheduleRef.current();
      }
    };

    const handlePreview = (event) => {
      window.clearTimeout(timerRef.current);
      setImageFailed(false);
      setPreviewSettings(
        event.detail ? normalizeScreensaverSettings(event.detail) : null,
      );
      visibleRef.current = true;
      setVisible(true);
    };

    const handleActivity = () => {
      if (visibleRef.current) dismissRef.current();
      else scheduleRef.current();
    };

    let lastPointerMove = 0;
    const handlePointerMove = (event) => {
      if (event.pointerType === "touch") return;
      const now = Date.now();
      if (now - lastPointerMove < 500) return;
      lastPointerMove = now;
      handleActivity();
    };

    const handleVisibility = () => {
      if (document.hidden) window.clearTimeout(timerRef.current);
      else scheduleRef.current();
    };

    window.addEventListener(SCREENSAVER_CHANGED_EVENT, handleSettingsChanged);
    window.addEventListener(SHOW_SCREENSAVER_EVENT, handlePreview);
    window.addEventListener("storage", handleSettingsChanged);
    document.addEventListener("pointerdown", handleActivity, { passive: true });
    document.addEventListener("pointermove", handlePointerMove, { passive: true });
    document.addEventListener("touchstart", handleActivity, { passive: true });
    document.addEventListener("keydown", handleActivity);
    document.addEventListener("wheel", handleActivity, { passive: true });
    document.addEventListener("visibilitychange", handleVisibility);

    scheduleRef.current();

    return () => {
      window.clearTimeout(timerRef.current);
      window.removeEventListener(SCREENSAVER_CHANGED_EVENT, handleSettingsChanged);
      window.removeEventListener(SHOW_SCREENSAVER_EVENT, handlePreview);
      window.removeEventListener("storage", handleSettingsChanged);
      document.removeEventListener("pointerdown", handleActivity);
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("touchstart", handleActivity);
      document.removeEventListener("keydown", handleActivity);
      document.removeEventListener("wheel", handleActivity);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    setImageFailed(false);
    scheduleRef.current();
  }, [settings]);

  useEffect(() => {
    if (!visible) return undefined;

    setNow(new Date());
    const clock = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(clock);
  }, [visible]);

  if (!visible) return null;

  const time = new Intl.DateTimeFormat("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(now);
  const date = new Intl.DateTimeFormat("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(now);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Salvapantallas"
      onPointerDown={dismissRef.current}
      onTouchStart={dismissRef.current}
      onContextMenu={(event) => event.preventDefault()}
      data-screensaver="true"
      className="fixed inset-0 z-[100] flex h-screen w-screen select-none items-center justify-center overflow-hidden overscroll-none bg-neutral-900 px-6"
      style={{ backgroundColor, color: foregroundColor }}
    >
      {imageSource && !imageFailed && (
        <img
          src={imageSource}
          alt=""
          onError={() => setImageFailed(true)}
          className="absolute inset-0 h-full w-full object-cover opacity-45"
        />
      )}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: overlayColor }}
      />

      <div className="relative z-10 flex max-w-2xl flex-col items-center text-center">
        <p
          className="text-sm font-semibold uppercase tracking-[0.35em]"
          style={{ color: mutedColor }}
        >
          {shop?.name || "AGEST TPV"}
        </p>

        {displayedSettings.showClock && (
          <>
            <div className="mt-6 text-7xl font-light tracking-tight sm:text-8xl">
              {time}
            </div>
            <p
              className="mt-2 text-lg capitalize"
              style={{ color: mutedColor }}
            >
              {date}
            </p>
          </>
        )}

        <div
          className="mt-10 h-px w-16"
          style={{ backgroundColor: lineColor }}
        />
        <p className="mt-6 text-base sm:text-lg" style={{ color: foregroundColor }}>
          {displayedSettings.message}
        </p>
        <div
          className="mt-5 flex items-center gap-2 text-xs"
          style={{ color: subtleColor }}
        >
          <Icon icon="mdi:gesture-tap" className="text-lg" />
          Toca para volver
        </div>
      </div>
    </div>
  );
}
